import React, { createRef } from "react";
import { Container, Table } from "react-bootstrap";
import { BaseModal, BaseNavTabs } from "react-epharma-components/lib";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { ILog, IUserDataSnapshot } from "@interfaces/log";
import { UserStatusLabels } from "@enums/UserStatusEnum";
import { UserGenderLabels } from "@enums/UserGenderEnum";
import Utils from "@utils";

interface IModalLog {
  showModalLog: boolean;
  type?: string;
  log: ILog[];
  setShowModalLog: (value: boolean) => void;
}

export function ModalLog(props: IModalLog) {
  const { log, showModalLog, setShowModalLog } = props;
  const navTabsRef = createRef<BaseNavTabs>();

  const dateTime = new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });

  const formatValue = (val: any) => {
    if (val === null || val === undefined || val === "") return <i>(vazio)</i>;
    return val;
  };

  const renderFieldDiff = (item: ILog, field: keyof IUserDataSnapshot, isDate: boolean = false) => {
    let oldVal = item.userDataOld ? item.userDataOld[field] : null;
    let newVal = item.userDataNew ? item.userDataNew[field] : null;

    if (isDate) {
      oldVal = Utils.formatDate(oldVal as string);
      newVal = Utils.formatDate(newVal as string);
    } else if (field === "cpf") {
      oldVal = Utils.maskCpf(String(oldVal || ""));
      newVal = Utils.maskCpf(String(newVal || ""));
    } else if (field === "mobilePhoneNumber") {
      oldVal = Utils.maskPhone(String(oldVal || ""));
      newVal = Utils.maskPhone(String(newVal || ""));
    } else if (field === "status") {
      const oldNum = oldVal !== null && oldVal !== undefined ? Number(oldVal) : null;
      const newNum = newVal !== null && newVal !== undefined ? Number(newVal) : null;

      oldVal = oldNum !== null && UserStatusLabels[oldNum as keyof typeof UserStatusLabels] ? UserStatusLabels[oldNum as keyof typeof UserStatusLabels] : oldVal;
      newVal = newNum !== null && UserStatusLabels[newNum as keyof typeof UserStatusLabels] ? UserStatusLabels[newNum as keyof typeof UserStatusLabels] : newVal;
    } else if (field === "gender") {
      const oldNum = oldVal !== null && oldVal !== undefined ? Number(oldVal) : null;
      const newNum = newVal !== null && newVal !== undefined ? Number(newVal) : null;

      oldVal = oldNum !== null && UserGenderLabels[oldNum as keyof typeof UserGenderLabels] ? UserGenderLabels[oldNum as keyof typeof UserGenderLabels] : oldVal;
      newVal = newNum !== null && UserGenderLabels[newNum as keyof typeof UserGenderLabels] ? UserGenderLabels[newNum as keyof typeof UserGenderLabels] : newVal;
    }

    if (oldVal === newVal) {
      if (newVal === "") return formatValue(newVal);
      return <span className="text-muted">{formatValue(newVal)}</span>;
    }

    return (
      <div className="d-flex align-items-center flex-wrap">
        <span className="text-danger text-decoration-line-through me-1" style={{ fontSize: "0.75rem" }} title="Valor Anterior">
          {formatValue(oldVal)}
        </span>

        <FontAwesomeIcon icon={faArrowRight} className="text-muted mx-1" size="xs" />

        <span className="text-success fw-bold ms-1" title="Novo Valor">
          {formatValue(newVal)}
        </span>
      </div>
    );
  };

  const sortedLog = log
    ? [...log].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      })
    : [];

  const TabWrapper: React.FC<any> = ({ load, onSelect, children }) => <>{children}</>;

  return (
    <BaseModal
      name="LogModal"
      size="xl"
      show={showModalLog}
      title="Log de Auditoria - Usuário"
      content={
        <div id="ItensLog">
          <BaseNavTabs
            ref={navTabsRef}
            navs={[
              {
                key: "UserData",
                name: "Dados Cadastrais",
                content: (
                  <TabWrapper>
                    <Container>
                      {sortedLog && sortedLog.length > 0 ? (
                        <Table responsive striped hover size="sm">
                          <thead className="bg-light">
                            <tr>
                              <th>Autor</th>
                              <th>Data</th>
                              <th>CPF</th>
                              <th>Nome</th>
                              <th>Sobrenome</th>
                              <th>E-mail</th>
                              <th>Celular</th>
                              <th>Data Nasc.</th>
                              <th>Gênero</th>
                              <th>Status</th>
                              <th>Reset Senha</th>
                              <th>Justificativa</th>
                            </tr>
                          </thead>
                          <tbody style={{ fontSize: "0.9rem" }}>
                            {sortedLog.map((item, index) => {
                              return (
                                <tr key={item.id || index}>
                                  <td style={{ minWidth: "150px" }}>{item.authorName}</td>
                                  <td style={{ minWidth: "160px" }}>{item.createdAt ? dateTime.format(new Date(item.createdAt)) : "-"}</td>
                                  <td style={{ minWidth: "150px" }}>{renderFieldDiff(item, "cpf")}</td>
                                  <td style={{ minWidth: "180px" }}>{renderFieldDiff(item, "name")}</td>
                                  <td style={{ minWidth: "180px" }}>{renderFieldDiff(item, "lastName")}</td>
                                  <td style={{ minWidth: "250px" }}>{renderFieldDiff(item, "email")}</td>
                                  <td style={{ minWidth: "160px" }}>{renderFieldDiff(item, "mobilePhoneNumber")}</td>
                                  <td style={{ minWidth: "130px" }}>{renderFieldDiff(item, "birthDate", true)}</td>
                                  <td style={{ minWidth: "130px" }}>{renderFieldDiff(item, "gender")}</td>
                                  <td style={{ minWidth: "130px" }}>{renderFieldDiff(item, "status")}</td>
                                  <td style={{ minWidth: "120px" }}>{item.isPasswordReset ? <span className="text-success fw-bold">Sim</span> : <span className="text-muted">Não</span>}</td>
                                  <td style={{ minWidth: "400px" }}>{item.justification}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="alert alert-warning mt-2 px-2" role="alert">
                          <FontAwesomeIcon className="text-warning" size="lg" icon={faInfoCircle} /> Nenhum registro encontrado
                        </div>
                      )}
                    </Container>
                  </TabWrapper>
                ),
              },
            ]}
            defaultActiveKey="UserData"
          />
        </div>
      }
      callbackAfterClose={() => setShowModalLog(!showModalLog)}
    />
  );
}
